import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import HeadlessDialog from "@components/Dialog";
import ScrollableModalBody from "@components/ScrollableModalBody";
import { IPlantDTO } from "@interfaces/IPlantDTO";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import savePlantSchema from "@validators/savePlantSchema";
import Button from "@components/Button";
import FormikInput from "@components/form/FormikInput";
import {
  createPlant,
  savePlant,
  fetchEngagementStatusbyID,
  savePlantConfiguration,
} from "@service/PlantService";
import Spinner from "@components/Spinner";
import axiosClient from "@service/axiosClient";
import { IPlantConfigurationDTO } from "@interfaces/IPlantConfigurationDTO";
import FormikCombobox from "@components/form/FormikCombobox";
import { getStatesWithCombinedValue } from "@utils/states";
import { ISelect } from "@interfaces/ISelect";
import FormikDatepicker from "@components/form/FormikDatepicker";
interface IPlantEditProps {
  id: number;
  selectedPlant: IPlantDTO;
  isOpen: boolean;
  onClose: () => void;
  roleGroups: ISelect[];
  engagementStatuses: ISelect[];
  companies: ISelect[];
  clients: ISelect[];
}

interface IPlantForm {
  name: string;
  location: string;
  admin_fee: number;
  plantconfiguration_id: number;
  external_id: string;
  state: string;
  state_code: string;
  plant_code: string;
  contractor_seq: number;
  plant_id: number;
  roleGroup: string;
  role_group_id: number;
  engagementStatus?: string;
  engagementStartDate?: Date;
  company: string;
  company_id: number;
  client: string;
  client_id: number;
  // Invoice section (bill-to party + tax rate)
  company_name: string;
  company_name_abbreviation: string;
  invoice_state: string;
  street_address: string;
  city: string;
  zip_code: string;
  phone_number: string;
  tax: number | string;
}

export function PlantCreateEdit({
  id,
  selectedPlant,
  isOpen,
  onClose,
  roleGroups,
  engagementStatuses,
  companies,
  clients,
}: IPlantEditProps): JSX.Element {
  const { t } = useTranslation();
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [isLoading, setIsLoading] = useState(true);

  const [engagementData, setEngagementData] = useState<{
    engagementStatus: string;
    engagementStartDate: Date;
  } | null>(null);

  const [configuration, setConfiguration] = useState<IPlantConfigurationDTO>({
    id: 0,
    external_id: "",
    state: "",
    state_code: "",
    plant_code: "",
    contractor_seq: 0,
    plant_id: id,
    company_id: 0,
    client_id: 0,
  });
  const [states, setStates] = useState<ISelect[]>([]);
  useEffect(() => {
    async function fetchPlantConfiguration() {
      try {
        const response = (await axiosClient.get(
          `/plantconfiguration_api/plant/${id}`
        )) as { data: IPlantConfigurationDTO };
        if (response.data) {
          setConfiguration(response.data);
        }
      } catch (error) {
        console.error("Error fetching plant configuration:", error);
      }
    }
    if (id > 0) {
      fetchPlantConfiguration();
    }
  }, [id]);

  useEffect(() => {
    const statesWithCombinedValue = getStatesWithCombinedValue();
    // Map to ISelect by adding a unique id (e.g., index)
    setStates(
      statesWithCombinedValue.map((state, idx) => ({
        id: idx,
        value: state.value,
        code: state.code,
      })) as ISelect[]
    );
  }, []);

  const getStateValueByCode = useCallback(
    (stateCode: string): string => {
      const state = states.find((s) => s.code === stateCode);
      return state ? state.value : "";
    },
    [states]
  );

  const getStateCodeByValue = useCallback(
    (stateValue: string): string => {
      const state = states.find((s) => s.value === stateValue);
      return state?.code ? state.code : "";
    },
    [states]
  );

  const getRoleGroupNameById = useCallback(
    (roleGroupId: number): string => {
      const roleGroup = roleGroups.find((c) => c.id === roleGroupId);
      return roleGroup ? roleGroup.value : "";
    },
    [roleGroups]
  );

  const getRoleGroupIdByName = useCallback(
    (roleGroupName: string): number | undefined => {
      const roleGroup = roleGroups.find((c) => c.value === roleGroupName);
      return roleGroup ? roleGroup.id : undefined;
    },
    [roleGroups]
  );

  const getCompanyNameById = useCallback(
    (companyId: number): string => {
      const company = companies.find((c) => c.id === companyId);
      return company ? company.value : "";
    },
    [companies]
  );

  const getCompanyIdByName = useCallback(
    (companyName: string): number | undefined => {
      const company = companies.find((c) => c.value === companyName);
      return company ? company.id : undefined;
    },
    [companies]
  );

  const getClientNameById = useCallback(
    (clientId: number): string => {
      const client = clients.find((c) => c.id === clientId);
      return client ? client.value : "";
    },
    [clients]
  );

  const getClientIdByName = useCallback(
    (clientName: string): number | undefined => {
      const client = clients.find((c) => c.value === clientName);
      return client ? client.id : undefined;
    },
    [clients]
  );
  const initialValues =
    id > 0 && selectedPlant
      ? {
          name: selectedPlant.name,
          location: selectedPlant.location,
          admin_fee: selectedPlant.admin_fee
            ? parseFloat((selectedPlant.admin_fee * 100).toFixed(2))
            : 0,
          plantconfiguration_id: configuration.id,
          external_id: configuration.external_id,
          state: getStateValueByCode(configuration.state_code),
          state_code: configuration.state_code || "",
          plant_code: configuration.plant_code,
          contractor_seq: configuration.contractor_seq,
          plant_id: configuration.plant_id,
          roleGroup: getRoleGroupNameById(selectedPlant.role_group_id || 0),
          role_group_id: selectedPlant.role_group_id || 0,
          engagementStatus:
            engagementData?.engagementStatus || "active_non_billable",
          engagementStartDate: engagementData?.engagementStartDate
            ? engagementData.engagementStartDate
            : new Date(),
          company: getCompanyNameById(configuration.company_id || 0),
          company_id: configuration.company_id || 0,
          client: getClientNameById(configuration.client_id || 0),
          client_id: configuration.client_id || 0,
          company_name: selectedPlant.company_name ?? "",
          company_name_abbreviation:
            selectedPlant.company_name_abbreviation ?? "",
          invoice_state: selectedPlant.state ?? "",
          street_address: selectedPlant.street_address ?? "",
          city: selectedPlant.city ?? "",
          zip_code: selectedPlant.zip_code ?? "",
          phone_number: selectedPlant.phone_number ?? "",
          tax: selectedPlant.tax ?? "",
        }
      : {
          name: "",
          location: "",
          admin_fee: 0,
          plantconfiguration_id: 0,
          external_id: "",
          state: "",
          state_code: "",
          plant_code: "",
          contractor_seq: 0,
          plant_id: 0,
          roleGroup: "",
          role_group_id: 0,
          engagementStatus:
            engagementData?.engagementStatus || "active_non_billable",
          engagementStartDate:
            engagementData?.engagementStartDate || new Date(),
          company: "",
          company_id: 0,
          client: "",
          client_id: 0,
          company_name: "",
          company_name_abbreviation: "",
          invoice_state: "",
          street_address: "",
          city: "",
          zip_code: "",
          phone_number: "",
          tax: "",
        };

  const plantValidationSchema = savePlantSchema(t).shape(
    id > 0
      ? {
          // When editing (id > 0), remove validation for fields that won't be saved
          external_id: Yup.string().notRequired(),
          roleGroup: Yup.string().notRequired(),
          state: Yup.string().notRequired(),
          plant_code: Yup.string().notRequired(),
          contractor_seq: Yup.number().notRequired(),
        }
      : {}
  );

  useEffect(() => {
    async function fetchEngagement() {
      try {
        if (id > 0) {
          const data = {
            plant_id: id,
          };
          const response = await fetchEngagementStatusbyID(data);
          setEngagementData({
            engagementStatus: response.engagementStatus,
            engagementStartDate: response.engagementStartDate,
          });
        }
      } catch (error) {
        console.error("Error fetching engagement status by ID:", error);
      } finally {
        setIsLoading(false);
      }
    }
    if (isLoading) {
      fetchEngagement();
    }
  }, [id, isLoading]);

  const handlePlantSubmit = useCallback(
    async (
      values: IPlantForm,
      { setSubmitting }: import("formik").FormikHelpers<IPlantForm>
    ) => {
      const invoiceFields: Partial<IPlantDTO> = {
        company_name: values.company_name,
        company_name_abbreviation: values.company_name_abbreviation,
        state: values.invoice_state,
        street_address: values.street_address,
        city: values.city,
        zip_code: values.zip_code,
        phone_number: values.phone_number,
        tax:
          values.tax === "" || values.tax === null
            ? undefined
            : Number(values.tax),
      };
      if (id > 0) {
        try {
          let data: IPlantDTO = {
            id: id,
            name: values.name,
            location: values.location,
            admin_fee: values.admin_fee
              ? parseFloat(Number(values.admin_fee).toFixed(2))
              : 0,
            role_group_id: getRoleGroupIdByName(values.roleGroup),
            engagementStatus: values.engagementStatus,
            engagementStartDate: values.engagementStartDate,
            ...invoiceFields,
          };
          await savePlant(data as Partial<IPlantDTO>);

          // Update plant configuration
          let dataConfiguration: IPlantConfigurationDTO = {
            id: values.plantconfiguration_id,
            external_id: values.external_id,
            state_code: getStateCodeByValue(values.state),
            plant_code: values.plant_code,
            contractor_seq: Number(values.contractor_seq),
            plant_id: id,
            company_id: getCompanyIdByName(values.company),
            client_id: getClientIdByName(values.client),
          };
          await savePlantConfiguration(
            dataConfiguration as Partial<IPlantConfigurationDTO>
          );

          onClose();
          triggerSnackbar(`Success updating plant`);
        } catch (err: any) {
          // console.log(err);
          triggerSnackbar(`Error updating plant: ${err?.response.data.error}`);
        } finally {
          setSubmitting(false);
        }
      } else {
        try {
          let data: IPlantDTO = {
            name: values.name,
            location: values.location,
            admin_fee: values.admin_fee
              ? parseFloat(Number(values.admin_fee).toFixed(2))
              : 0,
            role_group_id: getRoleGroupIdByName(values.roleGroup),
            engagementStatus: values.engagementStatus,
            engagementStartDate: values.engagementStartDate,
            ...invoiceFields,
          };
          const response = await createPlant(data);
          // Ensure response.data is actually an IPlantDTO
          const newPlant = response.data as unknown as IPlantDTO;
          if (!newPlant.id) {
            throw new Error("Failed to create plant, no ID returned");
          }
          let dataConfiguration: IPlantConfigurationDTO = {
            id: 0,
            external_id: values.external_id,
            state_code: getStateCodeByValue(values.state),
            plant_code: values.plant_code,
            contractor_seq: Number(values.contractor_seq),
            plant_id: newPlant.id,
            company_id: getCompanyIdByName(values.company),
            client_id: getClientIdByName(values.client),
          };
          await savePlantConfiguration(
            dataConfiguration as Partial<IPlantConfigurationDTO>
          );
          onClose();
          triggerSnackbar(`Success creating plant`);
        } catch (err: any) {
          triggerSnackbar(`Error creating plant: ${err?.message}`);
        } finally {
          setSubmitting(false);
        }
      }
    },
    [
      id,
      getRoleGroupIdByName,
      onClose,
      triggerSnackbar,
      getStateCodeByValue,
      getCompanyIdByName,
      getClientIdByName,
    ]
  );

  return (
    <>
      {isOpen && (
        <HeadlessDialog
          title={t("Plant.title")}
          description=""
          isOpen={isOpen}
          setIsOpen={onClose}
          onClose={onClose}
          buttonText="Close"
          size="tw-w-[100vw] md:tw-max-w-[40vw] tw-min-h-[95vh]"
        >
          <div className="tw-text-base tw-font-bold tw-text-primary-light">
            <span className="tw-text-xs tw-bg-gray-300 tw-p-1 tw-rounded">
              {id > 0 ? "EDIT Plant" : "NEW Plant"}
            </span>
          </div>
          <ScrollableModalBody className="tw-overflow-y-scroll">
            {!isLoading ? (
              <Formik<IPlantForm>
                key={`${id}-${engagementData?.engagementStatus}-${engagementData?.engagementStartDate}`}
                initialValues={initialValues}
                validationSchema={plantValidationSchema}
                onSubmit={handlePlantSubmit}
                enableReinitialize={true}
              >
                {({ errors, touched, isSubmitting }) => (
                  <div className="tw-px-4 tw-pb-4 tw-text-sm" data-testid="form">
                    <Form>
                      <div className="tw-border tw-p-3 tw-border-gray-500">
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-mt-1">
                          <div className="md:tw-col-span-2">
                            <Field
                              name="name"
                              label={t("Plant.name")}
                              as={FormikInput}
                              error={touched.name && errors.name}
                            />
                          </div>
                          <Field
                            name="location"
                            label={t("Plant.location")}
                            as={FormikInput}
                            error={touched.location && errors.location}
                          />
                          <Field
                            name="admin_fee"
                            label={t("Plant.admin_fee")}
                            as={FormikInput}
                            inputMode="numeric"
                            error={touched.admin_fee && errors.admin_fee}
                          />
                          <Field
                            name="engagementStatus"
                            label={t("Plant.engagementStatus")}
                            options={engagementStatuses}
                            as={FormikCombobox}
                            error={
                              touched.engagementStatus &&
                              errors.engagementStatus
                            }
                          />
                          <Field
                            name="engagementStartDate"
                            label={t("Plant.engagementStartDate")}
                            as={FormikDatepicker}
                            error={
                              touched.engagementStartDate &&
                              errors.engagementStartDate
                            }
                          />
                          <div className="md:tw-col-span-2 tw-mt-1 tw-border-t tw-border-gray-300" />
                          <Field
                            name="external_id"
                            label={t("Plant.monday")}
                            as={FormikInput}
                            disabled={id > 0}
                            error={touched.external_id && errors.external_id}
                          />
                          <Field
                            name="roleGroup"
                            label={t("Plant.roleGroup")}
                            options={roleGroups}
                            as={FormikCombobox}
                            disabled={id > 0}
                            error={touched.roleGroup && errors.roleGroup}
                          />
                          <Field
                            name="state"
                            label={t("Plant.state_code")}
                            options={states}
                            as={FormikCombobox}
                            disabled={id > 0}
                            error={touched.state && errors.state}
                          />

                          {id > 0 && (
                            <Field
                              name="plant_code"
                              label={t("Plant.plant_code")}
                              as={FormikInput}
                              disabled={id > 0}
                              error={touched.plant_code && errors.plant_code}
                            />
                          )}
                          {id > 0 && (
                            <Field
                              name="contractor_seq"
                              label={t("Plant.contractor_seq")}
                              as={FormikInput}
                              inputMode="numeric"
                              type="number"
                              disabled={id > 0}
                              error={
                                touched.contractor_seq && errors.contractor_seq
                              }
                            />
                          )}
                          <div className="md:tw-col-span-2 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                            <Field
                              name="company"
                              label={t("Plant.company")}
                              options={companies}
                              as={FormikCombobox}
                              error={touched.company && errors.company}
                            />
                            <Field
                              name="client"
                              label={t("Plant.client")}
                              options={clients}
                              as={FormikCombobox}
                              error={touched.client && errors.client}
                            />
                          </div>

                          {/* Invoice section: bill-to party + tax rate
                              printed/used on AR invoices. */}
                          <div className="md:tw-col-span-2 tw-mt-2 tw-border-t tw-border-gray-300 tw-pt-3">
                            <h3 className="tw-text-sm tw-font-bold tw-color-primary">
                              Invoice
                            </h3>
                            <p className="tw-text-xs tw-text-gray-500">
                              Bill-to details and tax rate used when printing /
                              generating AR invoices.
                            </p>
                          </div>
                          <div className="md:tw-col-span-2">
                            <Field
                              name="company_name"
                              label="Company Name"
                              as={FormikInput}
                              error={touched.company_name && errors.company_name}
                            />
                          </div>
                          <Field
                            name="company_name_abbreviation"
                            label="Company Abbreviation"
                            as={FormikInput}
                            error={
                              touched.company_name_abbreviation &&
                              errors.company_name_abbreviation
                            }
                          />
                          <Field
                            name="phone_number"
                            label="Phone"
                            as={FormikInput}
                            error={touched.phone_number && errors.phone_number}
                          />
                          <div className="md:tw-col-span-2">
                            <Field
                              name="street_address"
                              label="Street Address"
                              as={FormikInput}
                              error={
                                touched.street_address && errors.street_address
                              }
                            />
                          </div>
                          <Field
                            name="city"
                            label="City"
                            as={FormikInput}
                            error={touched.city && errors.city}
                          />
                          <Field
                            name="invoice_state"
                            label="State"
                            as={FormikInput}
                            error={touched.invoice_state && errors.invoice_state}
                          />
                          <Field
                            name="zip_code"
                            label="Zip Code"
                            as={FormikInput}
                            error={touched.zip_code && errors.zip_code}
                          />
                          <Field
                            name="tax"
                            label="Tax (%)"
                            as={FormikInput}
                            inputMode="numeric"
                            error={touched.tax && errors.tax}
                          />
                        </div>
                        <div className="tw-flex tw-grid-cols-1 md:tw-grid-cols-1 tw-justify-end tw-mt-3">
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="md:tw-w-[30%]"
                          >
                            {isSubmitting ? (
                              <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" />
                            ) : (
                              "Submit"
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </div>
                )}
              </Formik>
            ) : (
              <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
                <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" />
              </div>
            )}
          </ScrollableModalBody>
        </HeadlessDialog>
      )}
    </>
  );
}
